import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { useAuth } from "../../contexts/Auth";
import "./SignIn.scss";
import CircularProgress from "@mui/material/CircularProgress";
import logo from "./logoTa.png";
import { useSnackbar } from "notistack";
import TextField from "@mui/material/TextField";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useHistory } from "react-router-dom";
import InboxIcon from "@mui/icons-material/Inbox";
import { useCountdown } from "../../hooks/useCountdown";
import {
  fetchPasswordResetEligibility,
  trackPasswordResetAttempt,
} from "../../api/passwordReset";
import { phraseErrorMessage, formatSeconds } from "./helpers";
import { ResetPasswordErrorCodes } from "./constants";
import Alert from "@mui/material/Alert";

const resetPasswordTimer = 300;

const SignIn = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = new URLSearchParams(window.location.search);
  const emailQueryParam = queryParams.get("email") || "";
  const [email, setEmail] = useState(emailQueryParam);
  const { sendPasswordResetEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errored, _setErrored] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const history = useHistory();
  const { startTimer, secondsLeft, isRunning } = useCountdown();

  const resetPassword = async (email) => {
    setLoading(true);

    try {
      // First check if user is eligible for password reset
      const response = await fetchPasswordResetEligibility(email);
      if (response?.error_code) {
        setErrorCode(response.error_code);
        startTimer(response.retry_after_seconds);
        return;
      }

      // If eligible, proceed with sending password reset email
      var actionCodeSettings = {
        url: `https://${process.env.REACT_APP_REDIRECT_DOMAIN}/reset-password-form`,
        handleCodeInApp: false,
      };

      const success = await sendPasswordResetEmail(email, actionCodeSettings);

      if (success) {
        startTimer(resetPasswordTimer);
        setSubmitted(true);
        trackPasswordResetAttempt(email);
      } else {
        setErrorCode("DEFAULT_ERROR");
      }
    } catch (error) {
      setErrorCode("DEFAULT_ERROR");
      console.error("Error during password reset flow:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (email === "") return;
    resetPassword(email);
  };

  const goBackToLogin = () => {
    history.push("/signin");
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };

  const sendAgain = () => {
    setSubmitted(false);
    setErrorCode(null);
  };

  const isButtonDisabled = useMemo(() => {
    let result = true;
    if (email && validateEmail(email)) {
      result = false;
    }

    return result;
  }, [email]);

  useEffect(() => {
    if (!isRunning && secondsLeft < 1) {
      setErrorCode(null);
    }
  }, [isRunning, secondsLeft]);

  useEffect(() => {
    let message;
    let variant;
    if (errored) {
      message = "There was a problem resetting your password.";
      variant = "error";
    }
    // Only show non-rate-limiting errors in snackbar
    if (
      errorCode &&
      errorCode !== ResetPasswordErrorCodes.PASSWORD_RESET_ATTEMPT_LIMIT_REACHED
    ) {
      message = phraseErrorMessage(errorCode, secondsLeft);
      variant = "error";
    }
    if (submitted && !errored && !errorCode) {
      message = "Password reset email sent successfully!";
      variant = "success";
    }
    if (message?.length) enqueueSnackbar(message, { variant });
  }, [submitted, errored, errorCode]);

  return (
    <>
      <div className="sign_in__page">
        <div className="right-part">
          {submitted ? (
            <>
              <div className="confirm-view-top">
                <InboxIcon
                  className="inbox-icon"
                  style={{
                    fontSize: "120px",
                    fill: "#607D8B",
                    fillOpacity: 0.8,
                  }}
                />
                <div className="inbox-big-caption">Check your inbox</div>
                <div className="inbox-small-caption">
                  An email has been sent to <strong>{email}</strong> from the
                  TradeAlgo team. To reset your password, click on the link in
                  the email.
                </div>
                <div className="inbox-content">
                  Be sure to check your spam folder if you can&apos;t find the
                  email.
                  <br />
                  Haven&apos;t received it?{" "}
                  {secondsLeft === 0 ? (
                    <span className="send-again" onClick={sendAgain}>
                      Send again
                    </span>
                  ) : (
                    <span>
                      Send email again in{" "}
                      <strong>
                        <u>{formatSeconds(secondsLeft)}</u>
                      </strong>
                    </span>
                  )}
                </div>
              </div>
              <Button
                className="back-to-login-button"
                startIcon={
                  <ArrowBackIosNewIcon className="back-to-login-icon" />
                }
                onClick={goBackToLogin}
              >
                Back to Login
              </Button>
            </>
          ) : (
            <>
              <div className="sign-in-form-header">
                <img
                  className="sign-in-logo cursor-pointer"
                  alt=""
                  height={36}
                  src={logo}
                  onClick={goBackToLogin}
                />
                <div className="sign-in-form-header-content">
                  <div className="sign-in-form-header-content-title">
                    Reset your password
                  </div>
                  <div className="sign-in-form-header-content-detail">
                    Enter the email address you registered with
                  </div>
                </div>
              </div>
              <div className="sign-in-form-footer">
                <TextField
                  className="sign-in-email"
                  label="Email"
                  variant="standard"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={secondsLeft > 0 || loading}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !isButtonDisabled &&
                      errorCode === null
                    ) {
                      handleSubmit();
                    }
                  }}
                />

                {errorCode ===
                  ResetPasswordErrorCodes.PASSWORD_RESET_ATTEMPT_LIMIT_REACHED && (
                  <Alert
                    severity="error"
                    style={{ width: "100%", marginTop: "16px" }}
                  >
                    {phraseErrorMessage(errorCode, secondsLeft)}
                  </Alert>
                )}

                <Button
                  className="send-instruction-button"
                  variant="contained"
                  disabled={isButtonDisabled || errorCode !== null}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <CircularProgress size="30px" />
                  ) : (
                    "Send instructions"
                  )}
                </Button>

                <Button
                  className="back-to-login-button"
                  startIcon={
                    <ArrowBackIosNewIcon className="back-to-login-icon" />
                  }
                  onClick={goBackToLogin}
                >
                  Back to Login
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SignIn;
